import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useNationsAdmin } from "@/hooks/api/useNations";
import { useGameweeks } from "@/hooks/api/useGameweeks";
import type { Match, CreateMatchRequest, Phase } from "@/types/api.types";

interface MatchFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: Partial<Match> | null;
  isEditing: boolean;
  onSave: (data: CreateMatchRequest) => void;
  isSaving: boolean;
}

const PHASES: { value: Phase; label: string }[] = [
  { value: "GROUP_STAGE", label: "Group Stage" },
  { value: "ROUND_OF_32", label: "Round of 32" },
  { value: "ROUND_OF_16", label: "Round of 16" },
  { value: "QUARTER_FINAL", label: "Quarter Final" },
  { value: "SEMI_FINAL", label: "Semi Final" },
  { value: "THIRD_PLACE", label: "Third Place Match" },
  { value: "FINAL", label: "Final" },
];

const toDatetimeLocal = (isoString?: string) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

export default function MatchForm({
  open,
  onOpenChange,
  match,
  isEditing,
  onSave,
  isSaving,
}: MatchFormProps) {
  // Fetch all nations (backend defaults to 50 — not enough for FIFA's full list).
  // 500 is a safe upper bound; FIFA has ~210 member associations.
  const { data: nationsData } = useNationsAdmin({ limit: 500 });
  const { data: gameweeksData } = useGameweeks();
  const gameweeks = Array.isArray(gameweeksData) ? gameweeksData : [];

  const [selectedGameweekId, setSelectedGameweekId] = useState<string>("none");
  const [gwError, setGwError] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<CreateMatchRequest>({
    defaultValues: {
      homeNationId: undefined,
      awayNationId: undefined,
      phase: "GROUP_STAGE",
      kickoffTime: "",
      venue: "",
      stage: "",
    },
  });

  // Register Select-backed fields so RHF tracks their validation
  useEffect(() => {
    register("homeNationId", { required: "Home nation is required" });
    register("awayNationId", { required: "Away nation is required" });
    register("phase", { required: "Tournament phase is required" });
  }, [register]);

  useEffect(() => {
    if (match && isEditing) {
      reset({
        homeNationId: match.homeNationId,
        awayNationId: match.awayNationId,
        phase: match.phase,
        kickoffTime: toDatetimeLocal(match.kickoffTime),
        venue: match.venue || "",
        stage: match.stage || "",
      });
      setSelectedGameweekId(match.gameweekId ? String(match.gameweekId) : "none");
    } else {
      reset({
        homeNationId: undefined,
        awayNationId: undefined,
        phase: "GROUP_STAGE",
        kickoffTime: "",
        venue: "",
        stage: "",
      });
      setSelectedGameweekId("none");
    }
    setGwError("");
  }, [match, isEditing, reset]);

  const homeNationId = watch("homeNationId");
  const awayNationId = watch("awayNationId");

  const availableForAway = nationsData?.nations?.filter((n) => n.id !== Number(homeNationId));
  const availableForHome = nationsData?.nations?.filter((n) => n.id !== Number(awayNationId));

  const onSubmit = (data: CreateMatchRequest) => {
    // Manually guard Select fields in case RHF misses them (no DOM ref)
    let blocked = false;
    if (!data.homeNationId) {
      setError("homeNationId", { message: "Home nation is required" });
      blocked = true;
    }
    if (!data.awayNationId) {
      setError("awayNationId", { message: "Away nation is required" });
      blocked = true;
    }
    if (selectedGameweekId === "none") {
      setGwError("Matchweek is required");
      blocked = true;
    }
    if (blocked) return;

    onSave({
      ...data,
      gameweekId: Number(selectedGameweekId),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-gray-900">
            {isEditing ? "Edit Match" : "Schedule New Match"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Nations */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700 mb-2">
                Home Nation <span className="text-red-500">*</span>
              </Label>
              <Select
                value={watch("homeNationId")?.toString() || ""}
                onValueChange={(val) =>
                  setValue("homeNationId", Number(val), { shouldValidate: true })
                }
              >
                <SelectTrigger className={`bg-white ${errors.homeNationId ? "border-red-500" : "border-gray-300"}`}>
                  <SelectValue placeholder="Select home nation" />
                </SelectTrigger>
                <SelectContent>
                  {availableForHome?.map((nation) => (
                    <SelectItem key={nation.id} value={nation.id.toString()}>
                      <div className="flex items-center gap-2">
                        {nation.flagUrl && (
                          <img src={nation.flagUrl} className="w-5 h-3 object-cover rounded" alt="" />
                        )}
                        {nation.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.homeNationId && (
                <p className="text-sm text-red-500 mt-1">{errors.homeNationId.message}</p>
              )}
            </div>

            <div>
              <Label className="text-gray-700 mb-2">
                Away Nation <span className="text-red-500">*</span>
              </Label>
              <Select
                value={watch("awayNationId")?.toString() || ""}
                onValueChange={(val) =>
                  setValue("awayNationId", Number(val), { shouldValidate: true })
                }
              >
                <SelectTrigger className={`bg-white ${errors.awayNationId ? "border-red-500" : "border-gray-300"}`}>
                  <SelectValue placeholder="Select away nation" />
                </SelectTrigger>
                <SelectContent>
                  {availableForAway?.map((nation) => (
                    <SelectItem key={nation.id} value={nation.id.toString()}>
                      <div className="flex items-center gap-2">
                        {nation.flagUrl && (
                          <img src={nation.flagUrl} className="w-5 h-3 object-cover rounded" alt="" />
                        )}
                        {nation.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.awayNationId && (
                <p className="text-sm text-red-500 mt-1">{errors.awayNationId.message}</p>
              )}
            </div>
          </div>

          {/* Phase */}
          <div>
            <Label className="text-gray-700 mb-2">
              Tournament Phase <span className="text-red-500">*</span>
            </Label>
            <Select
              value={watch("phase") || "GROUP_STAGE"}
              onValueChange={(val) => setValue("phase", val as Phase, { shouldValidate: true })}
            >
              <SelectTrigger className={`bg-white ${errors.phase ? "border-red-500" : "border-gray-300"}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PHASES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.phase && (
              <p className="text-sm text-red-500 mt-1">{errors.phase.message}</p>
            )}
          </div>

          {/* Matchweek */}
          <div>
            <Label className="text-gray-700 mb-2">
              Matchweek <span className="text-red-500">*</span>
            </Label>
            <Select
              value={selectedGameweekId}
              onValueChange={(val) => {
                setSelectedGameweekId(val);
                if (val !== "none") setGwError("");
              }}
            >
              <SelectTrigger className={`bg-white ${gwError ? "border-red-500" : "border-gray-300"}`}>
                <SelectValue placeholder="Select matchweek…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Select a matchweek —</SelectItem>
                {gameweeks.map((gw) => (
                  <SelectItem key={gw.id} value={String(gw.id)}>
                    {gw.name}
                    <span className="ml-2 text-xs text-gray-400">({gw.status})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {gwError ? (
              <p className="text-sm text-red-500 mt-1">{gwError}</p>
            ) : (
              <p className="text-xs text-gray-400 mt-1">
                Create matchweeks from the Matchweeks panel above the match table.
              </p>
            )}
          </div>

          {/* Kickoff Time */}
          <div>
            <Label htmlFor="kickoffTime" className="text-gray-700">
              Kickoff Time <span className="text-red-500">*</span>
            </Label>
            <Input
              id="kickoffTime"
              type="datetime-local"
              {...register("kickoffTime", { required: "Kickoff time is required" })}
              className={`bg-white ${errors.kickoffTime ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.kickoffTime && (
              <p className="text-sm text-red-500 mt-1">{errors.kickoffTime.message}</p>
            )}
          </div>

          {/* Venue */}
          <div>
            <Label htmlFor="venue" className="text-gray-700">
              Venue <span className="text-red-500">*</span>
            </Label>
            <Input
              id="venue"
              {...register("venue", { required: "Venue is required" })}
              placeholder="MetLife Stadium, New Jersey"
              className={`bg-white ${errors.venue ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.venue && (
              <p className="text-sm text-red-500 mt-1">{errors.venue.message}</p>
            )}
          </div>

          {/* Stage label */}
          <div>
            <Label htmlFor="stage" className="text-gray-700">
              Stage Label <span className="text-red-500">*</span>
            </Label>
            <Input
              id="stage"
              {...register("stage", { required: "Stage label is required" })}
              placeholder="e.g. Group A - Matchday 1"
              className={`bg-white ${errors.stage ? "border-red-500" : "border-gray-300"}`}
            />
            {errors.stage && (
              <p className="text-sm text-red-500 mt-1">{errors.stage.message}</p>
            )}
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : isEditing ? "Update Match" : "Schedule Match"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
